import { useCallback, useEffect, useMemo, useState } from "react";

import { useDebouncedEffect } from "../../../../hooks/useDebounceEffect";
import axios from "../../../api/axios";
import ApiRequestErrorHandler from "../../../global/ApiRequestErrorHandler";

import { MIN_SEARCH_LENGTH, buildRawAddress, suggestionToAddressPayload } from "./helpers";

export const useClientAddressAutoComplete = ({
  address,
  setAddress,
  defaultAddress,
  inputSx = {},
  inputRequired,
  showAddressDetails = true,
}) => {
  const [searchVal, setSearchVal] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationResponse, setValidationResponse] = useState(
    !address ? false : Boolean(address.validated)
  );
  const [isError, setIsError] = useState(false);
  const [validAddressChange, setValidAddressChange] = useState(false);
  const [smartyError, setSmartyError] = useState(false);

  const isAuthorized = Boolean(process.env.REACT_APP_SMARTY_API_KEY);

  useEffect(() => {
    if (!defaultAddress) {
      return;
    }

    setAddress(defaultAddress);
    setValidationResponse(false);
  }, [defaultAddress, setAddress]);

  const resetSearchState = useCallback(() => {
    setSuggestions([]);
    setSmartyError(false);
    setLoading(false);
  }, []);

  const clearAddressSelection = useCallback(() => {
    setAddress(null);
    setSearchVal("");
    resetSearchState();
    setIsError(false);
    setValidationResponse(false);
    setValidAddressChange(false);
  }, [resetSearchState, setAddress]);

  const manualAddressChange = useCallback(
    (value, key, currentAddress) => {
      if (!currentAddress) {
        return;
      }

      setValidAddressChange(true);
      setAddress({ ...currentAddress, [key]: value });
    },
    [setAddress]
  );

  const handleSuggestionSelect = useCallback(
    async (suggestion) => {
      const payload = suggestionToAddressPayload(suggestion);

      if (!payload) {
        return;
      }

      setLoading(true);
      setSearchVal("");
      setSuggestions([]);

      try {
        const response = await axios.post(`/api/address/`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        setValidationResponse(true);
        setIsError(false);
        setSmartyError(false);
        setAddress(response.data);
      } catch (error) {
        const res = error.response?.data;
        const errArr = ApiRequestErrorHandler(res);
        setIsError(true);
        setValidationResponse(true);
        setSmartyError(false);
        console.log(errArr);
      } finally {
        setLoading(false);
      }
    },
    [setAddress]
  );

  const requestSuggestions = useCallback(
    async (query) => {
      const trimmedQuery = query.trim();

      if (!trimmedQuery || !isAuthorized) {
        resetSearchState();
        return;
      }

      if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
        resetSearchState();
        return;
      }

      setLoading(true);

      try {
        const response = await axios.get(
          `https://us-autocomplete-pro.api.smarty.com/lookup?key=${process.env.REACT_APP_SMARTY_API_KEY}&search=${encodeURIComponent(trimmedQuery)}&max_results=5`
        );

        const data = response.data?.suggestions ?? [];
        setSuggestions(data);
        setSmartyError(false);
      } catch (err) {
        console.log(err);
        setSmartyError(true);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [isAuthorized, resetSearchState]
  );

  useDebouncedEffect(
    () => {
      if (!searchVal) {
        resetSearchState();
        return;
      }

      requestSuggestions(searchVal);
    },
    [searchVal, requestSuggestions],
    750
  );

  const updateExistingAddress = useCallback(async () => {
    if (!address?.id) {
      return;
    }

    setLoading(true);

    const rawAddress = buildRawAddress(address);
    const updatedAddress = {
      ...address,
      raw: rawAddress,
      formatted: rawAddress,
    };

    setAddress(updatedAddress);

    try {
      const response = await axios.patch(`/api/address/${address.id}/`, updatedAddress, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      setValidationResponse(true);
      setIsError(false);
      setAddress(response.data);
      setValidAddressChange(false);
    } catch (error) {
      const res = error.response?.data;
      const errArr = ApiRequestErrorHandler(res);
      setIsError(true);
      setValidationResponse(true);
      console.log(errArr);
    } finally {
      setLoading(false);
    }
  }, [address, setAddress]);

  const handleSearch = useCallback(
    (val) => {
      setSearchVal(val);

      if (address) {
        setAddress(null);
        setValidationResponse(false);
        setIsError(false);
      }
    },
    [address, setAddress]
  );

  const showAddressAdded = Boolean(address && validationResponse && !isError);

  const textFieldSx = useMemo(() => {
    if (!showAddressAdded) {
      return inputSx;
    }

    const inputStyles = (inputSx && inputSx["& .MuiOutlinedInput-input"]) || {};

    return {
      ...(inputSx || {}),
      "& .MuiOutlinedInput-input": {
        ...inputStyles,
        paddingRight: inputStyles.paddingRight || "40px",
      },
    };
  }, [inputSx, showAddressAdded]);

  const helperText = useMemo(() => {
    if (!isAuthorized) {
      return "Unauthorized, please contact admin for support";
    }
    if (smartyError) {
      return "Something went wrong. If problem persists contact HnS admin";
    }
    if (isError) {
      return `Address invalid, please choose a valid address ${inputRequired ? "" : "or leave the field blank"}`;
    }
    if (searchVal.length > 0 && searchVal.length < MIN_SEARCH_LENGTH) {
      return "Search must be at least 5 characters";
    }
    return "";
  }, [isAuthorized, smartyError, isError, inputRequired, searchVal.length]);

  const shouldShowSuggestions = Boolean(searchVal && !smartyError && suggestions.length > 0);
  const canShowAddressDetails =
    showAddressDetails && Boolean(address) && (!defaultAddress || validationResponse);
  const showNoResults =
    !smartyError &&
    searchVal &&
    suggestions.length === 0 &&
    searchVal.length >= MIN_SEARCH_LENGTH &&
    !loading;

  const clearSearch = useCallback(() => setSearchVal(""), []);

  return {
    searchVal,
    suggestions,
    loading,
    helperText,
    textFieldSx,
    shouldShowSuggestions,
    canShowAddressDetails,
    showNoResults,
    manualAddressChange,
    handleSuggestionSelect,
    handleSearch,
    clearAddressSelection,
    clearSearch,
    updateExistingAddress,
    validAddressChange,
    isAuthorized,
    isError,
    smartyError,
    showAddressAdded,
    validationResponse,
  };
};
